import { useState, useRef, useEffect, type JSX } from 'react'
import { ChevronDown } from 'lucide-react'
import { useAgentStore, type UserStatusOption } from '../../store/agentStore.js'
import { getSocket } from '../../socket/client.js'
import { cn } from '../../utils/cn.js'

/**
 * Break codes are hidden for now — breaks are driven by the published WFM
 * schedule (BreakAssistant: scheduled breaks, deferral while on a call). The
 * codes are kept in the store so this flag can bring them back until they are
 * removed for good. Login/Available stay: agents still need to come online.
 */
const SHOW_BREAK_CODES = false

interface StatusDropdownProps {
  disabled?: boolean
}

export default function StatusDropdown({ disabled = false }: StatusDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const userStatus = useAgentStore((s) => s.userStatus)
  const allCodes = useAgentStore((s) => s.breakCodes)
  const breakCodes = SHOW_BREAK_CODES ? allCodes : allCodes.filter((c) => c.key === 'login' || c.key === 'available')
  const setUserStatus = useAgentStore((s) => s.setUserStatus)

  useEffect(() => {
    if (!isOpen) return
    function onClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [isOpen])

  const handleSelect = (option: UserStatusOption) => {
    if (option.disabled) return
    setUserStatus(option)
    if (option.key === 'available') {
      getSocket().emit('agent:status', { event: 'unpause' })
    } else if (!['login', 'available', 'logout'].includes(option.key)) {
      getSocket().emit('agent:status', { event: 'pause', reason: option.text })
    }
    setIsOpen(false)
  }

  const getStatusColor = (key: string) => {
    switch (key) {
      case 'login':
        return 'bg-slate-400'
      case 'available':
        return 'bg-emerald-500'
      default:
        return 'bg-orange-500'
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium transition-all',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-slate-300 hover:bg-slate-50',
        )}
      >
        <span className={cn('h-2 w-2 rounded-full', getStatusColor(userStatus.key))} />
        <span className="text-slate-900">{userStatus.text}</span>
        <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-[3px] border border-slate-200 bg-white py-1 shadow-xl">
          {breakCodes.map((option) => (
            <button
              key={option.key}
              onClick={() => handleSelect(option)}
              disabled={option.disabled}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors',
                option.disabled
                  ? 'cursor-not-allowed text-slate-400'
                  : 'text-slate-700 hover:bg-slate-50',
                userStatus.key === option.key && 'bg-blue-50 font-semibold text-blue-700',
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', getStatusColor(option.key))} />
              {option.text}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
